import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { AXIOS_INSTANCE, customInstance } from '../../services/api-client'
import type {
  RevenueRedshiftConnectorOption,
  RevenueRedshiftDataSourceConfig,
  RevenueRedshiftDataSourceUpdate,
} from '../revenue-close/redshiftDataSourceTypes'
import type { AgentReview, DeferredRevenueHealth, DeferredRevenueRun } from './types'

const BASE = '/api/deferred-revenue-scheduler'

export const deferredRevenueKeys = {
  all: ['deferred-revenue-scheduler'] as const,
  run: () => [...deferredRevenueKeys.all, 'run'] as const,
  health: () => [...deferredRevenueKeys.all, 'health'] as const,
  config: () => [...deferredRevenueKeys.all, 'config'] as const,
  connectors: () => [...deferredRevenueKeys.all, 'connectors'] as const,
  agentReview: () => [...deferredRevenueKeys.all, 'agent-review'] as const,
}

export function useDeferredRevenueRun(options?: { enabled?: boolean }) {
  return useQuery<DeferredRevenueRun>({
    queryKey: deferredRevenueKeys.run(),
    queryFn: () => customInstance({ url: `${BASE}/run`, method: 'GET' }),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  })
}

export function useDeferredRevenueHealth() {
  return useQuery<DeferredRevenueHealth>({
    queryKey: deferredRevenueKeys.health(),
    queryFn: () => customInstance({ url: `${BASE}/health`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useDeferredRevenueDataSourceConfig() {
  return useQuery<RevenueRedshiftDataSourceConfig>({
    queryKey: deferredRevenueKeys.config(),
    queryFn: () => customInstance({ url: `${BASE}/config/data-source`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useDeferredRevenueAvailableConnectors() {
  return useQuery<RevenueRedshiftConnectorOption[]>({
    queryKey: deferredRevenueKeys.connectors(),
    queryFn: () => customInstance({ url: `${BASE}/config/available-connectors`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useSetDeferredRevenueDataSource() {
  const queryClient = useQueryClient()
  return useMutation<RevenueRedshiftDataSourceConfig, Error, RevenueRedshiftDataSourceUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deferredRevenueKeys.all })
    },
  })
}

export function useClearDeferredRevenueDataSource() {
  const queryClient = useQueryClient()
  return useMutation<RevenueRedshiftDataSourceConfig, Error, void>({
    mutationFn: () =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deferredRevenueKeys.all })
    },
  })
}

export function useTestDeferredRevenueDataSource() {
  return useMutation<DeferredRevenueHealth, Error, RevenueRedshiftDataSourceUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data: payload,
      }),
  })
}

export function useDeferredRevenueAgentReview() {
  return useMutation<AgentReview, Error, { use_llm?: boolean }>({
    mutationKey: deferredRevenueKeys.agentReview(),
    mutationFn: body =>
      customInstance({
        url: `${BASE}/agent/review`,
        method: 'POST',
        data: { use_llm: body.use_llm ?? false },
      }),
  })
}

export async function downloadDeferredRevenueExport(exportType: 'roll' | 'journal' | 'audit') {
  const response = await AXIOS_INSTANCE.get(`${BASE}/exports/${exportType}`, {
    responseType: 'blob',
  })
  const disposition = response.headers['content-disposition'] || ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename =
    match?.[1] || {
      roll: 'OUTPUT_EC03e_Deferred_Revenue_Roll_Apr2025.csv',
      journal: 'OUTPUT_EC03e_NetSuite_JE_Import.csv',
      audit: 'OUTPUT_EC03e_Audit_Trail.csv',
    }[exportType]
  const url = window.URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
